import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ArrowLeftIcon } from '@heroicons/react/outline';
import { ImageUploadBox, ImageUploader } from '@service/seller/components/ImageUploadBox';
import {
  giftChoiceUnitListDataAtom,
  giftChoiceUnitListStatusAtom,
} from '@shared/api/giftChoiceUnit/giftChoiceUnit.atom';
import { useGiftChoiceUnitListHook } from '@shared/api/giftChoiceUnit/giftChoiceUnit.hook';
import {
  giftDeliveryCategoryListDataAtom,
  giftDeliveryCategoryListStatusAtom,
} from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.atom';
import { useGiftDeliveryCategoryListHook } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.hook';
import {
  giftItemBrandListDataAtom,
  giftItemBrandListStatusAtom,
} from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import {
  giftItemInfoDataAtomFamily,
  giftItemInfoStatusAtomFamily,
} from '@shared/api/giftItemInfo/giftItemInfo.atom';
import {
  createGiftItemInfo,
  deleteGiftItemInfo,
  updateGiftItemInfo,
} from '@shared/api/giftItemInfo/giftItemInfo.controller';
import { useGiftItemInfoHook } from '@shared/api/giftItemInfo/giftItemInfo.hook';
import {
  AdditionalOption,
  GiftItemInfoCreate,
  GiftItemInfoType,
  GiftItemInfoUpdate,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { postImage } from '@shared/api/uploadImage';
import { Button } from '@shared/components/Button';
import { Loading } from '@shared/components/Loading';
import { invalidateQueriesByUrlPart } from '@shared/hooks/recoil-query';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import styles from '../components/GiftBrandManagement.module.css';

const genKey = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `opt-${Math.random().toString(36).slice(2)}`;

// 추가옵션 복사/붙여넣기 클립보드 (상품 간 공유, localStorage 기반)
const OPTIONS_CLIPBOARD_KEY = 'giftItemInfo:additionalOptions:clipboard';

type ClipboardChoice = {
  name?: string;
  priceDelta?: number;
  description?: string;
  colorHex?: string;
  enabled?: boolean;
  descriptionImage?: Image | null;
};
type ClipboardOption = {
  name?: string;
  required?: boolean;
  selectionCount?: number;
  slotLabels?: string[];
  choices?: ClipboardChoice[];
};

/** 폼 내부에서 다루는 추가옵션 선택종류 (신규 파일/기존 이미지 상태 포함) */
interface OptionChoiceForm {
  key: string; // 렌더 안정용 로컬 키
  name: string;
  priceDelta: number;
  description: string; // 한 줄 설명
  colorHex: string; // 색상 핵사코드 (빈 문자열이면 미사용)
  enabled: boolean;
  descriptionImage: Image | null; // 기존 populated 이미지
  file: File | null; // 새로 선택한 파일
  preview: string; // 새 파일 object URL
}

/** 폼 내부에서 다루는 추가옵션 그룹 */
interface AdditionalOptionForm {
  key: string;
  name: string;
  required: boolean;
  selectionCount: number; // 선택 슬롯 수 (기본 1)
  slotLabels: string[]; // 슬롯별 라벨 (선택, 비우면 name+번호로 생성)
  choices: OptionChoiceForm[];
}

const createEmptyChoice = (): OptionChoiceForm => ({
  key: genKey(),
  name: '',
  priceDelta: 0,
  description: '',
  colorHex: '',
  enabled: true,
  descriptionImage: null,
  file: null,
  preview: '',
});

const createEmptyOption = (): AdditionalOptionForm => ({
  key: genKey(),
  name: '',
  required: false,
  selectionCount: 1,
  slotLabels: [],
  choices: [createEmptyChoice()],
});

/** 폼 내부에서 다루는 이미지 항목 (기존 업로드 이미지 또는 새로 선택한 파일) */
interface ImageItem {
  key: string; // 렌더 안정용 로컬 키
  image: Image | null; // 기존 업로드 이미지(populated)
  file: File | null; // 새로 선택한 파일
  preview: string; // 새 파일 object URL
}

// 새 파일들을 ImageItem 으로 변환 (object URL 미리보기 생성)
const filesToImageItems = (files: File[]): ImageItem[] =>
  files.map((file) => ({
    key: genKey(),
    image: null,
    file,
    preview: URL.createObjectURL(file),
  }));

// 이미지 항목 순서 이동
const moveImageItem = (
  setter: React.Dispatch<React.SetStateAction<ImageItem[]>>,
  key: string,
  dir: -1 | 1,
) =>
  setter((prev) => {
    const idx = prev.findIndex((i) => i.key === key);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= prev.length) return prev;
    const arr = [...prev];
    const tmp = arr[idx] as ImageItem;
    arr[idx] = arr[next] as ImageItem;
    arr[next] = tmp;
    return arr;
  });

// 이미지 항목 제거 (새 파일이면 object URL 정리)
const removeImageItem = (
  setter: React.Dispatch<React.SetStateAction<ImageItem[]>>,
  key: string,
) =>
  setter((prev) => {
    const target = prev.find((i) => i.key === key);
    if (target?.preview) URL.revokeObjectURL(target.preview);
    return prev.filter((i) => i.key !== key);
  });

// 이미지 항목 표시 URL (새 파일 우선, 없으면 기존)
const imageItemUrl = (item: ImageItem, variant: 'raw' | 'w400') =>
  item.preview || (item.image ? fileBucketUrl(item.image.url, variant) : '');

interface FormData {
  type: GiftItemInfoType;
  giftItemBrand: string; // 상품 브랜드 ID
  giftDeliveryCategory: string; // 배송 카테고리 ID
  subCategory: string; // 하위분류 이름 (카테고리에 하위분류가 있을 때만 사용)
  name: string;
  priceRegular: number;
  priceRetail: number;
  priceWhole: number;
  isEnabled: boolean;
  isSelectable: boolean; // 선택형 상품 여부
  isTaxable: boolean; // 과세여부
  detailInfo: string;
  tags: string;
  // 쿠폰 상세
  couponValidPeriod: string;
  couponPlaceOfExchange: string;
  // 배송 상세
  deliveryShippingInfo: string;
  deliveryShippingFeeInfo: string;
  deliveryShippingFee: number | ''; // 배송비 (원)
  deliveryFreeShippingMinQuantity: number | ''; // 무료배송 최소수량
  allowIndividualDelivery: boolean; // 개별배송 허용 여부 (false면 일괄배송 전용)
  useThanksPrint: '' | 'true' | 'false'; // 감사카드 사용 오버라이드 ('' = 브랜드 따름)
  // 주문 설정
  minOrderQuantity: number;
  orderUnit: number;
  order: number | ''; // 표시 순서
  productCode: string | ''; // 상품코드
}

export const GiftItemInfoFormPage: FC = function GiftItemInfoFormPage() {
  const { itemInfoId } = useParams<{ itemInfoId?: string }>();
  const isEdit = itemInfoId && itemInfoId !== 'new';
  const navigate = useNavigate();

  const { sellerRole } = useAuth();

  // Hooks
  useGiftItemInfoHook(isEdit ? itemInfoId ?? '' : '', sellerRole());
  useGiftItemBrandListHook(sellerRole());
  useGiftDeliveryCategoryListHook(sellerRole());
  useGiftChoiceUnitListHook(sellerRole());

  // State
  const giftItemInfoStatus = useRecoilValue(
    giftItemInfoStatusAtomFamily(itemInfoId ?? ''),
  );
  const giftItemInfoData = useRecoilValue(giftItemInfoDataAtomFamily(itemInfoId ?? ''));

  // 브랜드 목록
  const brandListStatus = useRecoilValue(giftItemBrandListStatusAtom);
  const brandList = useRecoilValue(giftItemBrandListDataAtom);

  // 배송 카테고리 목록
  const deliveryCategoryListStatus = useRecoilValue(giftDeliveryCategoryListStatusAtom);
  const deliveryCategoryList = useRecoilValue(giftDeliveryCategoryListDataAtom);

  // 선택유닛 목록
  const choiceUnitListStatus = useRecoilValue(giftChoiceUnitListStatusAtom);
  const choiceUnitList = useRecoilValue(giftChoiceUnitListDataAtom);

  const [formData, setFormData] = useState<FormData>({
    type: GiftItemInfoType.Delivery,
    giftItemBrand: '',
    giftDeliveryCategory: '',
    subCategory: '',
    name: '',
    priceRegular: 0,
    priceRetail: 0,
    priceWhole: 0,
    isEnabled: true,
    isSelectable: false,
    isTaxable: false,
    detailInfo: '',
    tags: '',
    couponValidPeriod: '',
    couponPlaceOfExchange: '',
    deliveryShippingInfo: '',
    deliveryShippingFeeInfo: '',
    deliveryShippingFee: '',
    deliveryFreeShippingMinQuantity: '',
    allowIndividualDelivery: true,
    useThanksPrint: '',
    minOrderQuantity: 1,
    orderUnit: 1,
    order: '',
    productCode: '',
  });

  // 선택된 브랜드
  const selectedBrand = useMemo(
    () => brandList.find((b) => b._id === formData.giftItemBrand),
    [brandList, formData.giftItemBrand],
  );

  // 선택유닛 선택 상태
  const [selectedChoiceUnits, setSelectedChoiceUnits] = useState<string[]>([]);

  // 허용 감사카드 템플릿 선택 상태 (빈 배열 = 브랜드 전체 허용)
  const [thanksPrintTemplateIds, setThanksPrintTemplateIds] = useState<string[]>([]);

  // 선택 브랜드의 감사카드 템플릿 목록 (허용 목록 체크박스용, order 순)
  const brandThanksTemplates = useMemo(() => {
    const list = selectedBrand?.thanksPrint?.templates ?? [];
    return [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [selectedBrand]);

  const toggleThanksTemplate = (id: string) => {
    setThanksPrintTemplateIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // 추가옵션 상태
  const [additionalOptions, setAdditionalOptions] = useState<AdditionalOptionForm[]>([]);

  // 언마운트 시 생성한 object URL 정리
  useEffect(() => {
    return () => {
      additionalOptions.forEach((opt) =>
        opt.choices.forEach((c) => c.preview && URL.revokeObjectURL(c.preview)),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 옵션 그룹 헬퍼
  const addOption = () => setAdditionalOptions((prev) => [...prev, createEmptyOption()]);

  const removeOption = (optKey: string) =>
    setAdditionalOptions((prev) => {
      const target = prev.find((o) => o.key === optKey);
      target?.choices.forEach((c) => c.preview && URL.revokeObjectURL(c.preview));
      return prev.filter((o) => o.key !== optKey);
    });

  const updateOption = (optKey: string, patch: Partial<AdditionalOptionForm>) =>
    setAdditionalOptions((prev) =>
      prev.map((o) => (o.key === optKey ? { ...o, ...patch } : o)),
    );

  const moveOption = (optKey: string, dir: -1 | 1) =>
    setAdditionalOptions((prev) => {
      const idx = prev.findIndex((o) => o.key === optKey);
      const next = idx + dir;
      if (idx < 0 || next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      const tmp = arr[idx] as AdditionalOptionForm;
      arr[idx] = arr[next] as AdditionalOptionForm;
      arr[next] = tmp;
      return arr;
    });

  // 선택종류 헬퍼
  const addChoice = (optKey: string) =>
    setAdditionalOptions((prev) =>
      prev.map((o) =>
        o.key === optKey ? { ...o, choices: [...o.choices, createEmptyChoice()] } : o,
      ),
    );

  const removeChoice = (optKey: string, choiceKey: string) =>
    setAdditionalOptions((prev) =>
      prev.map((o) => {
        if (o.key !== optKey) return o;
        const target = o.choices.find((c) => c.key === choiceKey);
        if (target?.preview) URL.revokeObjectURL(target.preview);
        return { ...o, choices: o.choices.filter((c) => c.key !== choiceKey) };
      }),
    );

  const updateChoice = (
    optKey: string,
    choiceKey: string,
    patch: Partial<OptionChoiceForm>,
  ) =>
    setAdditionalOptions((prev) =>
      prev.map((o) =>
        o.key === optKey
          ? {
              ...o,
              choices: o.choices.map((c) =>
                c.key === choiceKey ? { ...c, ...patch } : c,
              ),
            }
          : o,
      ),
    );

  const moveChoice = (optKey: string, choiceKey: string, dir: -1 | 1) =>
    setAdditionalOptions((prev) =>
      prev.map((o) => {
        if (o.key !== optKey) return o;
        const idx = o.choices.findIndex((c) => c.key === choiceKey);
        const next = idx + dir;
        if (idx < 0 || next < 0 || next >= o.choices.length) return o;
        const arr = [...o.choices];
        const tmp = arr[idx] as OptionChoiceForm;
        arr[idx] = arr[next] as OptionChoiceForm;
        arr[next] = tmp;
        return { ...o, choices: arr };
      }),
    );

  // 선택종류 설명 이미지 파일 선택 (object URL 미리보기)
  const handleChoiceFile = (optKey: string, choiceKey: string, file: File) => {
    const url = URL.createObjectURL(file);
    setAdditionalOptions((prev) =>
      prev.map((o) =>
        o.key === optKey
          ? {
              ...o,
              choices: o.choices.map((c) => {
                if (c.key !== choiceKey) return c;
                if (c.preview) URL.revokeObjectURL(c.preview);
                return { ...c, file, preview: url };
              }),
            }
          : o,
      ),
    );
  };

  // 선택종류 설명 이미지 표시 URL (새 파일 우선, 없으면 기존)
  const choiceImageUrl = (c: OptionChoiceForm) =>
    c.preview || (c.descriptionImage ? fileBucketUrl(c.descriptionImage.url, 'raw') : '');

  // 추가옵션 복사/붙여넣기 (localStorage) — 클립보드에 담긴 옵션 그룹 수
  const [clipboardCount, setClipboardCount] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(OPTIONS_CLIPBOARD_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  });

  // 현재 옵션을 클립보드에 복사 (파일 미리보기는 제외, 기존 이미지 참조는 유지)
  const handleCopyOptions = () => {
    if (additionalOptions.length === 0) return;
    const serializable = additionalOptions.map((o) => ({
      name: o.name,
      required: o.required,
      selectionCount: o.selectionCount,
      slotLabels: o.slotLabels,
      choices: o.choices.map((c) => ({
        name: c.name,
        priceDelta: c.priceDelta,
        description: c.description,
        colorHex: c.colorHex,
        enabled: c.enabled,
        descriptionImage: c.descriptionImage, // 기존 업로드 이미지(Image) 참조 유지
      })),
    }));
    try {
      localStorage.setItem(OPTIONS_CLIPBOARD_KEY, JSON.stringify(serializable));
      setClipboardCount(serializable.length);
      alert(`추가옵션 ${serializable.length}개를 복사했습니다.`);
    } catch {
      alert('복사에 실패했습니다.');
    }
  };

  // 클립보드의 옵션을 현재 목록 끝에 붙여넣기
  const handlePasteOptions = () => {
    let parsed: unknown;
    try {
      const raw = localStorage.getItem(OPTIONS_CLIPBOARD_KEY);
      parsed = raw ? JSON.parse(raw) : null;
    } catch {
      parsed = null;
    }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      alert('복사된 추가옵션이 없습니다.');
      return;
    }
    const pasted: AdditionalOptionForm[] = (parsed as ClipboardOption[]).map((o) => ({
      key: genKey(),
      name: o?.name ?? '',
      required: o?.required ?? false,
      selectionCount: Math.max(1, o?.selectionCount ?? 1),
      slotLabels: Array.isArray(o?.slotLabels) ? o.slotLabels : [],
      choices: Array.isArray(o?.choices)
        ? o.choices.map((c) => ({
            key: genKey(),
            name: c?.name ?? '',
            priceDelta: c?.priceDelta ?? 0,
            description: c?.description ?? '',
            colorHex: c?.colorHex ?? '',
            enabled: c?.enabled ?? true,
            descriptionImage:
              c?.descriptionImage && typeof c.descriptionImage === 'object'
                ? (c.descriptionImage as Image)
                : null,
            file: null,
            preview: '',
          }))
        : [],
    }));
    setAdditionalOptions((prev) => [...prev, ...pasted]);
    alert(`추가옵션 ${pasted.length}개를 붙여넣었습니다.`);
  };

  //* 선택유닛 검색·필터 — 유닛이 수백 개라 스크롤만으로는 못 찾는다(이름/브랜드 검색이 본체)
  const [unitSearchQuery, setUnitSearchQuery] = useState<string>('');
  const [unitBrandFilter, setUnitBrandFilter] = useState<string>('all');
  const [priceFilterMin, setPriceFilterMin] = useState<string>('');
  const [priceFilterMax, setPriceFilterMax] = useState<string>('');
  const [showSelectedUnitsOnly, setShowSelectedUnitsOnly] = useState(false);
  //* 판매중단 유닛은 기본 숨김(고를 일이 없다) — 이미 선택된 유닛만 예외로 계속 보인다
  const [hideDisabledUnits, setHideDisabledUnits] = useState(true);

  //* 기본값에서 하나라도 벗어나 있으면 '필터 초기화'를 노출
  const hasUnitFilter =
    unitSearchQuery.trim() !== '' ||
    unitBrandFilter !== 'all' ||
    priceFilterMin !== '' ||
    priceFilterMax !== '' ||
    showSelectedUnitsOnly ||
    !hideDisabledUnits;

  const resetUnitFilters = () => {
    setUnitSearchQuery('');
    setUnitBrandFilter('all');
    setPriceFilterMin('');
    setPriceFilterMax('');
    setShowSelectedUnitsOnly(false);
    setHideDisabledUnits(true);
  };

  const filteredChoiceUnitList = useMemo(() => {
    if (!choiceUnitList) return [];
    const min = priceFilterMin !== '' ? Number(priceFilterMin) : null;
    const max = priceFilterMax !== '' ? Number(priceFilterMax) : null;
    //* 공백으로 끊은 토큰 전부 포함(AND) — "스타벅스 아메리카노"처럼 브랜드+상품명 혼합 검색용
    const tokens = unitSearchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return choiceUnitList.filter((unit) => {
      const isSelected = selectedChoiceUnits.includes(unit._id);
      if (showSelectedUnitsOnly && !isSelected) return false;
      if (hideDisabledUnits && unit.enabled === false && !isSelected) return false;

      if (unitBrandFilter !== 'all') {
        const brandId =
          unit.giftItemBrand && typeof unit.giftItemBrand === 'object'
            ? unit.giftItemBrand._id
            : unit.giftItemBrand;
        if (unitBrandFilter === 'none' ? !!brandId : brandId !== unitBrandFilter) {
          return false;
        }
      }

      const price = unit.priceRegular ?? 0;
      if (min !== null && price < min) return false;
      if (max !== null && price > max) return false;

      if (tokens.length > 0) {
        const brandName =
          unit.giftItemBrand && typeof unit.giftItemBrand === 'object'
            ? unit.giftItemBrand.name
            : '';
        const haystack = `${unit.name} ${brandName} ${unit.description ?? ''} ${
          unit.priceRegular ?? ''
        }`.toLowerCase();
        if (!tokens.every((token) => haystack.includes(token))) return false;
      }
      return true;
    });
  }, [
    choiceUnitList,
    priceFilterMin,
    priceFilterMax,
    unitSearchQuery,
    unitBrandFilter,
    showSelectedUnitsOnly,
    hideDisabledUnits,
    selectedChoiceUnits,
  ]);

  //* 표시된(필터된) 유닛이 전부 선택돼 있는지 — 일괄 버튼 라벨/동작 판단용
  const allFilteredSelected =
    filteredChoiceUnitList.length > 0 &&
    filteredChoiceUnitList.every((unit) => selectedChoiceUnits.includes(unit._id));

  //* 일괄 선택은 '좁혀놓은 결과'에만 의미가 있어 필터가 걸렸고 30개 이하일 때만 노출
  const canBulkToggleUnits =
    hasUnitFilter &&
    filteredChoiceUnitList.length > 0 &&
    filteredChoiceUnitList.length <= 30;

  const toggleAllFilteredUnits = () => {
    const ids = filteredChoiceUnitList.map((unit) => unit._id);
    setSelectedChoiceUnits((prev) =>
      allFilteredSelected
        ? prev.filter((id) => !ids.includes(id))
        : [...prev, ...ids.filter((id) => !prev.includes(id))],
    );
  };

  //* 다른 탭에서 유닛을 등록/수정하고 온 뒤 이 목록만 다시 불러온다
  const queryClient = useQueryClient();
  const refreshChoiceUnitList = () => {
    void invalidateQueriesByUrlPart(queryClient, 'gift-choice-unit');
  };

  // 선택유닛 변경 시 정가를 선택된 유닛의 최대 priceRegular로 자동 설정
  // 단, 수정 모드 최초 로드 시점에는 저장된 정가를 유지(1회 스킵)하여 덮어쓰지 않는다.
  useEffect(() => {
    if (!formData.isSelectable || selectedChoiceUnits.length === 0) return;
    if (isEdit && !priceAutoSyncReadyRef.current) {
      priceAutoSyncReadyRef.current = true;
      return;
    }
    const maxPrice = selectedChoiceUnits.reduce((max, unitId) => {
      const unit = choiceUnitList.find((u) => u._id === unitId);
      return Math.max(max, unit?.priceRegular ?? 0);
    }, 0);
    setFormData((prev) => ({ ...prev, priceRegular: maxPrice }));
  }, [selectedChoiceUnits, formData.isSelectable, choiceUnitList, isEdit]);

  // 이미지 관련 state
  const [imageFile, setImageFile] = useState<File | null>(null);
  // 헤더/상세 이미지: 기존 이미지와 새 파일을 한 목록에서 함께 다룬다(추가/순서변경/삭제)
  const [headerImageItems, setHeaderImageItems] = useState<ImageItem[]>([]);
  const [detailImageItems, setDetailImageItems] = useState<ImageItem[]>([]);

  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  // 수정 모드 최초 1회만 폼에 로드 (refetch로 인한 입력 덮어쓰기/반복 실행 방지)
  const initializedRef = useRef(false);
  // 정가 자동 동기화가 수정 로드 직후 저장값을 덮어쓰지 않도록 1회 스킵
  const priceAutoSyncReadyRef = useRef(false);

  // 모달 상태
  const openId = `giftItemInfo-${itemInfoId ?? 'new'}`;
  const headerImageOpenId = `headerImageUploader-${openId}`;
  const setHeaderImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`imageUploader-${headerImageOpenId}`),
  );
  const setDetailImageUploaderOpen = useSetRecoilState(
    simpleOpenAtomFamily(`imageUploader-${openId}`),
  );

  // 수정 모드일 때 최초 1회만 데이터 로드
  useEffect(() => {
    if (initializedRef.current) return;
    if (isEdit && giftItemInfoStatus === 'success' && giftItemInfoData) {
      setFormData({
        type: giftItemInfoData.type || GiftItemInfoType.Delivery,
        giftItemBrand:
          typeof giftItemInfoData.giftItemBrand === 'object'
            ? giftItemInfoData.giftItemBrand?._id || ''
            : giftItemInfoData.giftItemBrand || '',
        giftDeliveryCategory:
          typeof giftItemInfoData.giftDeliveryCategory === 'object'
            ? giftItemInfoData.giftDeliveryCategory?._id || ''
            : giftItemInfoData.giftDeliveryCategory || '',
        subCategory: giftItemInfoData.subCategory || '',
        name: giftItemInfoData.name || '',
        priceRegular: giftItemInfoData.priceRegular || 0,
        priceRetail: giftItemInfoData.priceRetail || 0,
        priceWhole: giftItemInfoData.priceWhole || 0,
        isEnabled: giftItemInfoData.isEnabled ?? true,
        isSelectable: giftItemInfoData.isSelectable ?? false,
        isTaxable: giftItemInfoData.isTaxable ?? true,
        detailInfo: giftItemInfoData.detailInfo || '',
        tags: giftItemInfoData.tags?.join(', ') || '',
        couponValidPeriod: giftItemInfoData.couponDetail?.validPeriod || '',
        couponPlaceOfExchange: giftItemInfoData.couponDetail?.placeOfExchange || '',
        deliveryShippingInfo: giftItemInfoData.deliveryDetail?.shippingInfo || '',
        deliveryShippingFeeInfo: giftItemInfoData.deliveryDetail?.shippingFeeInfo || '',
        deliveryShippingFee: giftItemInfoData.deliveryDetail?.shippingFee ?? '',
        deliveryFreeShippingMinQuantity:
          giftItemInfoData.deliveryDetail?.freeShippingMinQuantity ?? '',
        allowIndividualDelivery: giftItemInfoData.allowIndividualDelivery ?? true,
        useThanksPrint:
          giftItemInfoData.useThanksPrint == null
            ? ''
            : giftItemInfoData.useThanksPrint
            ? 'true'
            : 'false',
        minOrderQuantity: giftItemInfoData.minOrderQuantity || 1,
        orderUnit: giftItemInfoData.orderUnit || 1,
        order: giftItemInfoData.order ?? '',
        productCode: giftItemInfoData.productCode ?? '',
      });

      if (giftItemInfoData.choiceUnits && Array.isArray(giftItemInfoData.choiceUnits)) {
        setSelectedChoiceUnits(
          giftItemInfoData.choiceUnits.map((unit) =>
            typeof unit === 'object' ? unit._id : unit,
          ),
        );
      }

      // 허용 감사카드 템플릿 (ObjectId 문자열 배열)
      setThanksPrintTemplateIds(
        Array.isArray(giftItemInfoData.thanksPrintTemplates)
          ? giftItemInfoData.thanksPrintTemplates.map(String)
          : [],
      );

      // 로드로 인해 정가 자동 동기화가 곧 한 번 실행될지 판단.
      // 실행 예정이면 그 1회를 스킵(저장값 유지)하고, 아니면 바로 활성화해
      // 이후 사용자의 첫 선택부터 정가가 동기화되도록 한다.
      const loadedUnitCount = Array.isArray(giftItemInfoData.choiceUnits)
        ? giftItemInfoData.choiceUnits.length
        : 0;
      const willAutoSyncOnLoad =
        (giftItemInfoData.isSelectable ?? false) && loadedUnitCount > 0;
      priceAutoSyncReadyRef.current = !willAutoSyncOnLoad;

      // 추가옵션 로드 (order 순 정렬)
      if (Array.isArray(giftItemInfoData.additionalOptions)) {
        const loaded = [...giftItemInfoData.additionalOptions]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map<AdditionalOptionForm>((opt) => ({
            key: genKey(),
            name: opt.name ?? '',
            required: opt.required ?? false,
            selectionCount: Math.max(1, opt.selectionCount ?? 1),
            slotLabels: Array.isArray(opt.slotLabels) ? opt.slotLabels : [],
            choices: [...(opt.choices ?? [])]
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map<OptionChoiceForm>((c) => ({
                key: genKey(),
                name: c.name ?? '',
                priceDelta: c.priceDelta ?? 0,
                description: c.description ?? '',
                colorHex: c.colorHex ?? '',
                enabled: c.enabled ?? true,
                descriptionImage:
                  c.descriptionImage && typeof c.descriptionImage === 'object'
                    ? (c.descriptionImage as Image)
                    : null,
                file: null,
                preview: '',
              })),
          }));
        setAdditionalOptions(loaded);
      }

      // 헤더/상세 이미지를 편집 가능한 항목 목록으로 로드
      if (Array.isArray(giftItemInfoData.headerImages)) {
        setHeaderImageItems(
          (giftItemInfoData.headerImages as Image[]).map((img) => ({
            key: genKey(),
            image: img,
            file: null,
            preview: '',
          })),
        );
      }
      if (Array.isArray(giftItemInfoData.detailImages)) {
        setDetailImageItems(
          (giftItemInfoData.detailImages as Image[]).map((img) => ({
            key: genKey(),
            image: img,
            file: null,
            preview: '',
          })),
        );
      }

      initializedRef.current = true;
    }
  }, [isEdit, giftItemInfoStatus, giftItemInfoData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (
      [
        'priceRegular',
        'priceRetail',
        'priceWhole',
        'minOrderQuantity',
        'orderUnit',
      ].includes(name)
    ) {
      setFormData((prev) => ({ ...prev, [name]: Number(value) }));
    } else if (
      ['order', 'deliveryShippingFee', 'deliveryFreeShippingMinQuantity'].includes(name)
    ) {
      setFormData((prev) => ({ ...prev, [name]: value === '' ? '' : Number(value) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // 업로드한 새 이미지는 기존 목록 뒤에 추가(덮어쓰지 않음)
  const onHeaderImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setHeaderImageItems((prev) => [...prev, ...filesToImageItems(files)]);
    setHeaderImageUploaderOpen(false);
  };

  const onDetailImageUploaderFunc = async (_data: unknown, files: File[]) => {
    setDetailImageItems((prev) => [...prev, ...filesToImageItems(files)]);
    setDetailImageUploaderOpen(false);
  };

  // 선택된 카테고리의 하위분류 목록 (있으면 subCategory 선택 UI 노출)
  const selectedCategorySubCategories =
    deliveryCategoryList.find((c) => c._id === formData.giftDeliveryCategory)
      ?.subCategories ?? [];

  // 카테고리 변경 시 하위분류 선택값 초기화
  const handleDeliveryCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      giftDeliveryCategory: e.target.value,
      subCategory: '',
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name) {
      alert('상품명을 입력해주세요.');
      return;
    }

    // 하위분류 검증: 선택한 카테고리에 하위분류가 있으면 필수
    const selectedCategory = deliveryCategoryList.find(
      (c) => c._id === formData.giftDeliveryCategory,
    );
    const categorySubs = selectedCategory?.subCategories ?? [];
    if (
      formData.giftDeliveryCategory &&
      categorySubs.length > 0 &&
      !categorySubs.includes(formData.subCategory)
    ) {
      alert('이 카테고리는 하위분류 선택이 필수입니다.');
      return;
    }
    // 카테고리가 없거나 하위분류가 없으면 subCategory는 비운다
    const effectiveSubCategory =
      formData.giftDeliveryCategory && categorySubs.includes(formData.subCategory)
        ? formData.subCategory
        : null;

    setSaveError('');
    setLoading(true);

    try {
      let imageId: string | undefined;
      let headerImageIds: string[] | undefined;
      let detailImageIds: string[] | undefined;

      // 대표 이미지 업로드
      if (imageFile) {
        const uploadedImage = await postImage(imageFile, sellerRole());
        imageId = uploadedImage._id;
      }

      // 헤더 이미지: 기존+신규를 화면 순서대로 전송. 신규 파일은 업로드 후 id로 변환.
      // 수정 모드에서는 빈 배열도 전송해 전체 삭제/순서변경을 반영한다.
      {
        const ids: string[] = [];
        for (const item of headerImageItems) {
          if (item.file) {
            const uploaded = await postImage(item.file, sellerRole());
            ids.push(uploaded._id);
          } else if (item.image) {
            ids.push(item.image._id);
          }
        }
        headerImageIds = isEdit ? ids : ids.length > 0 ? ids : undefined;
      }

      // 상세 이미지: 동일 방식
      {
        const ids: string[] = [];
        for (const item of detailImageItems) {
          if (item.file) {
            const uploaded = await postImage(item.file, sellerRole());
            ids.push(uploaded._id);
          } else if (item.image) {
            ids.push(item.image._id);
          }
        }
        detailImageIds = isEdit ? ids : ids.length > 0 ? ids : undefined;
      }

      const tagsArray = formData.tags
        ? formData.tags
            .split(',')
            .map((tag) => tag.trim())
            .filter(Boolean)
        : [];

      // 쿠폰 상세 정보
      const couponDetail =
        formData.type === GiftItemInfoType.Coupon &&
        (formData.couponValidPeriod || formData.couponPlaceOfExchange)
          ? {
              validPeriod: formData.couponValidPeriod,
              placeOfExchange: formData.couponPlaceOfExchange,
            }
          : undefined;

      // 배송 상세 정보
      const hasDeliveryDetail =
        formData.deliveryShippingInfo ||
        formData.deliveryShippingFeeInfo ||
        formData.deliveryShippingFee !== '' ||
        formData.deliveryFreeShippingMinQuantity !== '';
      const deliveryDetail = hasDeliveryDetail
        ? {
            ...(formData.deliveryShippingInfo && {
              shippingInfo: formData.deliveryShippingInfo,
            }),
            ...(formData.deliveryShippingFeeInfo && {
              shippingFeeInfo: formData.deliveryShippingFeeInfo,
            }),
            ...(formData.deliveryShippingFee !== '' && {
              shippingFee: Number(formData.deliveryShippingFee),
            }),
            ...(formData.deliveryFreeShippingMinQuantity !== '' && {
              freeShippingMinQuantity: Number(formData.deliveryFreeShippingMinQuantity),
            }),
          }
        : undefined;

      // 허용 감사카드 템플릿 — 선택 브랜드에 실존하는 템플릿 id만 유지
      // (브랜드 변경/템플릿 삭제로 남은 죽은 id가 주문 검증을 전부 막는 것 방지)
      const brandTemplateIdSet = new Set(
        (selectedBrand?.thanksPrint?.templates ?? [])
          .map((tpl) => tpl._id)
          .filter((id): id is string => Boolean(id)),
      );
      const effectiveThanksTemplates = selectedBrand?.thanksPrint
        ? thanksPrintTemplateIds.filter((id) => brandTemplateIdSet.has(id))
        : thanksPrintTemplateIds;
      const useThanksPrintValue =
        formData.useThanksPrint === '' ? null : formData.useThanksPrint === 'true';

      // 추가옵션 구성 — 신규 이미지 파일은 업로드 후 id로 변환, order는 화면 순서대로.
      // 이름 없는 옵션/선택종류는 제외한다. (배송형이 아니면 비움)
      const builtAdditionalOptions: AdditionalOption<string>[] = [];
      if (formData.type === GiftItemInfoType.Delivery) {
        for (let oi = 0; oi < additionalOptions.length; oi++) {
          const opt = additionalOptions[oi];
          if (!opt) continue;
          const optName = opt.name.trim();
          if (!optName) continue;

          const builtChoices = [];
          for (let ci = 0; ci < opt.choices.length; ci++) {
            const c = opt.choices[ci];
            if (!c) continue;
            const choiceName = c.name.trim();
            if (!choiceName) continue;

            let descriptionImageId = c.descriptionImage?._id;
            if (c.file) {
              const uploaded = await postImage(c.file, sellerRole());
              descriptionImageId = uploaded._id;
            }

            builtChoices.push({
              name: choiceName,
              priceDelta: Number(c.priceDelta) || 0,
              ...(c.description.trim() && { description: c.description.trim() }),
              ...(c.colorHex.trim() && { colorHex: c.colorHex.trim() }),
              ...(descriptionImageId && { descriptionImage: descriptionImageId }),
              order: ci,
              enabled: c.enabled,
            });
          }

          if (builtChoices.length === 0) continue;

          // 슬롯 수(기본 1). 2 이상이면 비운 슬롯 라벨은 제외하고 trim
          const selectionCount = Math.max(1, Math.floor(opt.selectionCount) || 1);
          const slotLabels = Array.from({ length: selectionCount }, (_, i) =>
            (opt.slotLabels[i] ?? '').trim(),
          );
          const hasSlotLabels = selectionCount > 1 && slotLabels.some((l) => l);

          builtAdditionalOptions.push({
            name: optName,
            required: opt.required,
            ...(selectionCount > 1 && { selectionCount }),
            ...(hasSlotLabels && { slotLabels }),
            order: oi,
            choices: builtChoices,
          });
        }
      }

      if (isEdit) {
        const updateData: GiftItemInfoUpdate = {
          _id: itemInfoId,
          type: formData.type,
          giftItemBrand: formData.giftItemBrand || null,
          giftDeliveryCategory: formData.giftDeliveryCategory || null,
          subCategory: effectiveSubCategory,
          additionalOptions: builtAdditionalOptions,
          name: formData.name,
          priceRegular: formData.priceRegular,
          priceRetail: formData.priceRetail,
          priceWhole: formData.priceWhole,
          isEnabled: formData.isEnabled,
          isSelectable: formData.isSelectable,
          isTaxable: formData.isTaxable,
          choiceUnits: formData.isSelectable ? selectedChoiceUnits : [],
          detailInfo: formData.detailInfo || undefined,
          tags: tagsArray,
          image: imageId,
          headerImages: headerImageIds,
          detailImages: detailImageIds,
          couponDetail,
          deliveryDetail,
          allowIndividualDelivery: formData.allowIndividualDelivery,
          useThanksPrint: useThanksPrintValue,
          thanksPrintTemplates: effectiveThanksTemplates,
          minOrderQuantity: formData.minOrderQuantity,
          orderUnit: formData.orderUnit,
          order: formData.order !== '' ? formData.order : undefined,
          productCode: formData.productCode !== '' ? formData.productCode : undefined,
        };
        await updateGiftItemInfo(updateData, sellerRole());
        alert('원상품이 수정되었습니다.');
      } else {
        const createData: GiftItemInfoCreate = {
          type: formData.type,
          giftItemBrand: formData.giftItemBrand || undefined,
          giftDeliveryCategory: formData.giftDeliveryCategory || undefined,
          subCategory: effectiveSubCategory || undefined,
          additionalOptions:
            builtAdditionalOptions.length > 0 ? builtAdditionalOptions : undefined,
          name: formData.name,
          priceRegular: formData.priceRegular,
          priceRetail: formData.priceRetail,
          priceWhole: formData.priceWhole,
          isEnabled: formData.isEnabled,
          isSelectable: formData.isSelectable,
          isTaxable: formData.isTaxable,
          choiceUnits: formData.isSelectable ? selectedChoiceUnits : [],
          detailInfo: formData.detailInfo || undefined,
          tags: tagsArray.length > 0 ? tagsArray : undefined,
          image: imageId,
          headerImages: headerImageIds,
          detailImages: detailImageIds,
          couponDetail,
          deliveryDetail,
          allowIndividualDelivery: formData.allowIndividualDelivery,
          ...(useThanksPrintValue !== null && { useThanksPrint: useThanksPrintValue }),
          ...(effectiveThanksTemplates.length > 0 && {
            thanksPrintTemplates: effectiveThanksTemplates,
          }),
          minOrderQuantity: formData.minOrderQuantity,
          orderUnit: formData.orderUnit,
          order: formData.order !== '' ? formData.order : undefined,
          productCode: formData.productCode !== '' ? formData.productCode : undefined,
        };
        await createGiftItemInfo(createData, sellerRole());
        alert('원상품이 등록되었습니다.');
      }
      navigate('/gift-seller/item-info');
    } catch (error) {
      console.error(error);
      setSaveError(
        '저장에 실패했습니다. 입력한 내용은 유지됩니다. 잠시 후 다시 시도해주세요.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;

    if (!confirm('정말 삭제하시겠습니까?')) return;

    setSaveError('');
    setLoading(true);
    try {
      await deleteGiftItemInfo(itemInfoId, sellerRole());
      alert('원상품이 삭제되었습니다.');
      navigate('/gift-seller/item-info');
    } catch (error) {
      console.error(error);
      setSaveError('삭제에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  // 기존 대표 이미지 URL
  const defaultImageUrl = useMemo(() => {
    if (giftItemInfoData?.image && typeof giftItemInfoData.image === 'object') {
      return (giftItemInfoData.image as Image).url;
    }
    return undefined;
  }, [giftItemInfoData]);

  // 새로 추가한 헤더/상세 이미지의 object URL을 언마운트 시 정리
  const newImagePreviewsRef = useRef<string[]>([]);
  useEffect(() => {
    newImagePreviewsRef.current = [...headerImageItems, ...detailImageItems]
      .map((i) => i.preview)
      .filter(Boolean);
  }, [headerImageItems, detailImageItems]);
  useEffect(() => {
    return () => {
      newImagePreviewsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  if (
    isEdit &&
    (giftItemInfoStatus === 'error' ||
      (giftItemInfoStatus === 'success' && !giftItemInfoData))
  ) {
    return (
      <div
        role="alert"
        className={`${styles.page} min-h-screen bg-gray-50 px-6 py-20 text-center`}
      >
        <h1 className="text-lg font-semibold">원상품을 불러오지 못했습니다.</h1>
        <p className="mt-2 text-sm text-gray-500">
          다시 시도하거나 목록에서 상품을 확인해주세요.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/gift-seller/item-info')}
            className="rounded border border-gray-300 bg-white px-4 py-2 text-sm"
          >
            목록으로
          </button>
          <button
            type="button"
            onClick={() =>
              void invalidateQueriesByUrlPart(queryClient, `gift-item-info/${itemInfoId}`)
            }
            className="rounded bg-indigo-600 px-4 py-2 text-sm text-white"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }
  if (isEdit && giftItemInfoStatus !== 'success') {
    return (
      <div
        role="status"
        aria-label="원상품 불러오는 중"
        className="center-box min-h-screen"
      >
        <Loading />
      </div>
    );
  }

  return (
    <div
      className={`${styles.page} min-h-screen bg-gray-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8 lg:py-8`}
    >
      <div className="mx-auto max-w-6xl">
        <header className="mb-7">
          <button
            type="button"
            disabled={loading}
            onClick={() => navigate('/gift-seller/item-info')}
            className="mb-4 inline-flex items-center gap-1.5 rounded text-sm text-gray-500 hover:text-gray-900 disabled:opacity-40"
          >
            <ArrowLeftIcon aria-hidden className="h-4 w-4" />
            원상품 목록
          </button>
          <h1 className="text-2xl font-semibold tracking-tight">
            {isEdit ? '원상품 수정' : '새 원상품 등록'}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            상품 기본정보와 가격을 입력하고, 판매에 필요한 설정을 추가하세요.
          </p>
        </header>
        <div className="grid items-start gap-6 xl:grid-cols-[180px_minmax(0,1fr)]">
          <nav
            aria-label="원상품 설정 바로가기"
            className="flex flex-wrap gap-1 xl:sticky xl:top-6 xl:flex-col"
          >
            <p className="mb-3 hidden px-3 text-xs font-medium text-gray-400 xl:block">
              설정 항목
            </p>
            {[
              { id: 'basic', label: '기본정보' },
              { id: 'pricing', label: '가격 · 주문' },
              { id: 'choices', label: '선택형 상품' },
              {
                id: 'delivery',
                label:
                  formData.type === GiftItemInfoType.Delivery
                    ? '배송 · 감사카드'
                    : '쿠폰 정보',
              },
              { id: 'images', label: '상품 이미지' },
              ...(formData.type === GiftItemInfoType.Delivery
                ? [{ id: 'options', label: '추가옵션' }]
                : []),
            ].map(({ id, label }) => (
              <a
                key={id}
                href={`#item-${id}`}
                className="rounded px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              >
                {label}
              </a>
            ))}
          </nav>
          <div className="min-w-0">
            <fieldset
              disabled={loading}
              aria-label={isEdit ? '원상품 수정' : '원상품 등록'}
              className="min-w-0 space-y-6"
            >
              <section
                id="item-basic"
                aria-labelledby="item-basic-heading"
                className="scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
              >
                <div className="mb-5">
                  <h2 id="item-basic-heading" className="text-base font-semibold">
                    기본정보
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    상품명은 필수입니다. 상품 유형과 브랜드, 분류를 함께 확인하세요.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {/* 상품코드 */}
                  <div>
                    <label
                      htmlFor="item-productCode"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      상품코드
                    </label>
                    <input
                      id="item-productCode"
                      name="productCode"
                      value={formData.productCode}
                      onChange={handleChange}
                      placeholder="상품코드를 입력하세요"
                      className="w-full rounded border bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                    />
                  </div>
                  {/* 상품명 */}
                  <div>
                    <label
                      htmlFor="item-name"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      상품명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="item-name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="상품명을 입력하세요"
                      className="w-full rounded border bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                      required
                    />
                  </div>
                  {/* 타입 + 활성화 */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        상품 타입
                      </label>
                      <div className="flex gap-4">
                        <label className="flex cursor-pointer items-center gap-1.5">
                          <input
                            type="radio"
                            name="type"
                            value={GiftItemInfoType.Delivery}
                            checked={formData.type === GiftItemInfoType.Delivery}
                            onChange={handleChange}
                            className="h-4 w-4"
                          />
                          <span className="text-sm">배송형</span>
                        </label>
                        <label className="flex cursor-pointer items-center gap-1.5">
                          <input
                            type="radio"
                            name="type"
                            value={GiftItemInfoType.Coupon}
                            checked={formData.type === GiftItemInfoType.Coupon}
                            onChange={handleChange}
                            className="h-4 w-4"
                          />
                          <span className="text-sm">쿠폰형</span>
                        </label>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex cursor-pointer items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">과세</span>
                        <input
                          type="checkbox"
                          name="isTaxable"
                          checked={formData.isTaxable}
                          onChange={handleChange}
                          className="h-4 w-4"
                        />
                      </label>
                      <label className="flex cursor-pointer items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">활성화</span>
                        <input
                          type="checkbox"
                          name="isEnabled"
                          checked={formData.isEnabled}
                          onChange={handleChange}
                          className="h-4 w-4"
                        />
                      </label>
                    </div>
                  </div>
                  {/* 상품 브랜드 */}
                  <div>
                    <label
                      htmlFor="item-giftItemBrand"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      상품 브랜드
                    </label>
                    <select
                      id="item-giftItemBrand"
                      name="giftItemBrand"
                      value={formData.giftItemBrand}
                      onChange={handleChange}
                      className="w-full rounded border bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                      disabled={brandListStatus !== 'success'}
                    >
                      <option value="">선택 안함</option>
                      {brandList.map((brand) => (
                        <option key={brand._id} value={brand._id}>
                          {brand.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-0.5 text-xs text-gray-400">
                      예: 스타벅스, 투썸플레이스 (선택 안함으로 브랜드 제거 가능)
                    </p>
                  </div>
                  {/* 배송 카테고리 */}
                  <div>
                    <label
                      htmlFor="item-giftDeliveryCategory"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      배송 카테고리
                    </label>
                    <select
                      id="item-giftDeliveryCategory"
                      name="giftDeliveryCategory"
                      value={formData.giftDeliveryCategory}
                      onChange={handleDeliveryCategoryChange}
                      className="w-full rounded border bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                      disabled={deliveryCategoryListStatus !== 'success'}
                    >
                      <option value="">선택 안함</option>
                      {deliveryCategoryList.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-0.5 text-xs text-gray-400">
                      배송 카테고리별 그룹핑에 사용됩니다.
                    </p>

                    {/* 하위분류: 선택한 카테고리에 하위분류가 있을 때만 노출(필수) */}
                    {selectedCategorySubCategories.length > 0 && (
                      <div className="mt-2">
                        <label
                          htmlFor="item-subCategory"
                          className="mb-1 block text-sm font-medium text-gray-700"
                        >
                          하위분류 <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="item-subCategory"
                          name="subCategory"
                          value={formData.subCategory}
                          onChange={handleChange}
                          className="w-full rounded border bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                        >
                          <option value="">선택하세요</option>
                          {selectedCategorySubCategories.map((sub) => (
                            <option key={sub} value={sub}>
                              {sub}
                            </option>
                          ))}
                        </select>
                        <p className="mt-0.5 text-xs text-gray-400">
                          이 카테고리는 하위분류 선택이 필수입니다.
                        </p>
                      </div>
                    )}
                  </div>
                  {/* 태그 */}
                  <div>
                    <label
                      htmlFor="item-tags"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      태그
                    </label>
                    <input
                      type="text"
                      id="item-tags"
                      name="tags"
                      value={formData.tags}
                      onChange={handleChange}
                      placeholder="쉼표로 구분 (예: 답례품, 인기상품)"
                      className="w-full rounded border bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                    />
                  </div>
                  {/* 상세 설명 */}
                  <div>
                    <label
                      htmlFor="item-detailInfo"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      상세 설명
                    </label>
                    <textarea
                      id="item-detailInfo"
                      name="detailInfo"
                      value={formData.detailInfo}
                      onChange={handleChange}
                      placeholder="상세 설명을 입력하세요"
                      className="h-24 w-full rounded border bg-white px-3 py-2 text-sm disabled:bg-gray-50"
                    />
                  </div>
                </div>
              </section>
              <section
                id="item-pricing"
                aria-labelledby="item-pricing-heading"
                className="scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
              >
                <div className="mb-5">
                  <h2 id="item-pricing-heading" className="text-base font-semibold">
                    가격 · 주문
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    가격은 원 단위로 입력합니다. 선택형 상품의 정가는 선택한 유닛의 최대
                    정가로 설정됩니다.
                  </p>
                </div>
                <div className="space-y-5">
                  {/* 가격 설정 */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      가격 설정 <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label
                          htmlFor="item-priceRegular"
                          className="mb-0.5 block text-xs text-gray-500"
                        >
                          정가
                        </label>
                        <input
                          type="number"
                          id="item-priceRegular"
                          name="priceRegular"
                          value={formData.priceRegular}
                          onChange={handleChange}
                          placeholder="0"
                          className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="item-priceRetail"
                          className="mb-0.5 block text-xs text-gray-500"
                        >
                          판매가
                        </label>
                        <input
                          type="number"
                          id="item-priceRetail"
                          name="priceRetail"
                          value={formData.priceRetail}
                          onChange={handleChange}
                          placeholder="0"
                          className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="item-priceWhole"
                          className="mb-0.5 block text-xs text-gray-500"
                        >
                          도매가
                        </label>
                        <input
                          type="number"
                          id="item-priceWhole"
                          name="priceWhole"
                          value={formData.priceWhole}
                          onChange={handleChange}
                          placeholder="0"
                          className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  {/* 주문 설정 */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      주문 설정
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label
                          htmlFor="item-minOrderQuantity"
                          className="mb-0.5 block text-xs text-gray-500"
                        >
                          최소주문수량
                        </label>
                        <input
                          type="number"
                          id="item-minOrderQuantity"
                          name="minOrderQuantity"
                          value={formData.minOrderQuantity}
                          onChange={handleChange}
                          min={1}
                          placeholder="1"
                          className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="item-orderUnit"
                          className="mb-0.5 block text-xs text-gray-500"
                        >
                          주문단위
                        </label>
                        <input
                          type="number"
                          id="item-orderUnit"
                          name="orderUnit"
                          value={formData.orderUnit}
                          onChange={handleChange}
                          min={1}
                          placeholder="1"
                          className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                        />
                        <p className="mt-0.5 text-xs text-gray-400">(수량 증감 단위)</p>
                      </div>
                      <div>
                        <label
                          htmlFor="item-order"
                          className="mb-0.5 block text-xs text-gray-500"
                        >
                          표시순서
                        </label>
                        <input
                          type="number"
                          id="item-order"
                          name="order"
                          value={formData.order}
                          onChange={handleChange}
                          min={0}
                          placeholder="미설정"
                          className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                        />
                        <p className="mt-0.5 text-xs text-gray-400">
                          (낮을수록 앞에 표시)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section
                id="item-choices"
                aria-labelledby="item-choices-heading"
                className="scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
              >
                <div className="mb-5">
                  <h2 id="item-choices-heading" className="text-base font-semibold">
                    선택형 상품
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    고객이 교환할 유닛을 지정하고 표시 순서를 관리합니다.
                  </p>
                </div>
                <div className="space-y-5">
                  {/* 선택형 상품 */}
                  <div>
                    <div className="flex items-center justify-between">
                      <div>
                        <label
                          htmlFor="item-isSelectable"
                          className="text-sm font-medium text-gray-700"
                        >
                          선택형 상품
                        </label>
                        <p className="text-xs text-gray-500">
                          고객이 교환 시 원하는 옵션을 선택할 수 있습니다
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        id="item-isSelectable"
                        name="isSelectable"
                        checked={formData.isSelectable}
                        onChange={handleChange}
                        className="h-5 w-5"
                      />
                    </div>

                    {/* 선택유닛 선택 (isSelectable일 때만) */}
                    {formData.isSelectable && (
                      <div className="mt-3 border-t border-gray-200 pt-3">
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          선택 가능한 유닛
                        </label>

                        {/* 검색·필터 — 유닛이 많아 스크롤만으로는 못 찾는다 */}
                        {choiceUnitListStatus === 'success' &&
                          choiceUnitList.length > 0 && (
                            <div className="mb-2 space-y-1.5">
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="relative min-w-[180px] flex-1">
                                  <input
                                    type="text"
                                    value={unitSearchQuery}
                                    onChange={(e) => setUnitSearchQuery(e.target.value)}
                                    aria-label="선택유닛 검색"
                                    placeholder="유닛명, 브랜드, 설명 검색..."
                                    className="w-full rounded border border-gray-200 bg-white py-1 pl-7 pr-7 text-xs disabled:bg-gray-50"
                                  />
                                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                                    🔍
                                  </span>
                                  {unitSearchQuery && (
                                    <button
                                      type="button"
                                      aria-label="선택유닛 검색어 지우기"
                                      onClick={() => setUnitSearchQuery('')}
                                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-600"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                                <select
                                  aria-label="선택유닛 브랜드"
                                  value={unitBrandFilter}
                                  onChange={(e) => setUnitBrandFilter(e.target.value)}
                                  disabled={brandListStatus !== 'success'}
                                  className="rounded border border-gray-200 bg-white px-2 py-1 text-xs disabled:bg-gray-50"
                                >
                                  <option value="all">전체 브랜드</option>
                                  <option value="none">브랜드 없음</option>
                                  {brandList.map((brand) => (
                                    <option key={brand._id} value={brand._id}>
                                      {brand.name}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-xs font-medium text-indigo-600">
                                  가격대
                                </span>
                                <input
                                  type="number"
                                  aria-label="선택유닛 최소 가격"
                                  value={priceFilterMin}
                                  onChange={(e) => setPriceFilterMin(e.target.value)}
                                  placeholder="최소"
                                  min={0}
                                  className="w-20 rounded border border-gray-200 bg-white px-2 py-1 text-xs disabled:bg-gray-50"
                                />
                                <span className="text-xs text-gray-400">~</span>
                                <input
                                  type="number"
                                  aria-label="선택유닛 최대 가격"
                                  value={priceFilterMax}
                                  onChange={(e) => setPriceFilterMax(e.target.value)}
                                  placeholder="최대"
                                  min={0}
                                  className="w-20 rounded border border-gray-200 bg-white px-2 py-1 text-xs disabled:bg-gray-50"
                                />
                                <span className="text-xs text-gray-400">원</span>
                                <label className="flex cursor-pointer items-center gap-1 text-xs text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={showSelectedUnitsOnly}
                                    onChange={(e) =>
                                      setShowSelectedUnitsOnly(e.target.checked)
                                    }
                                    className="h-3.5 w-3.5"
                                  />
                                  선택한 것만
                                </label>
                                <label className="flex cursor-pointer items-center gap-1 text-xs text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={hideDisabledUnits}
                                    onChange={(e) =>
                                      setHideDisabledUnits(e.target.checked)
                                    }
                                    className="h-3.5 w-3.5"
                                  />
                                  판매중만
                                </label>
                                {hasUnitFilter && (
                                  <button
                                    type="button"
                                    onClick={resetUnitFilters}
                                    className="rounded border border-gray-200 px-2 py-0.5 text-xs text-indigo-600 hover:bg-indigo-100"
                                  >
                                    필터 초기화
                                  </button>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-gray-500">
                                <span>
                                  {filteredChoiceUnitList.length}개 표시 (전체{' '}
                                  {choiceUnitList.length}개) · 선택{' '}
                                  <span className="font-medium text-indigo-600">
                                    {selectedChoiceUnits.length}
                                  </span>
                                  개
                                </span>
                                <span className="flex items-center gap-2">
                                  {canBulkToggleUnits && (
                                    <button
                                      type="button"
                                      onClick={toggleAllFilteredUnits}
                                      className="text-indigo-600 hover:underline"
                                    >
                                      {allFilteredSelected
                                        ? '표시된 유닛 해제'
                                        : '표시된 유닛 모두 선택'}
                                    </button>
                                  )}
                                  <a
                                    href="/gift-seller/choice-unit/new"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-indigo-600 hover:underline"
                                  >
                                    + 유닛 등록
                                  </a>
                                  <button
                                    type="button"
                                    onClick={refreshChoiceUnitList}
                                    className="text-gray-500 hover:underline"
                                  >
                                    목록 새로고침
                                  </button>
                                </span>
                              </div>
                            </div>
                          )}

                        {choiceUnitListStatus === 'success' &&
                        choiceUnitList.length > 0 ? (
                          <div className="max-h-72 space-y-2 overflow-y-auto rounded border border-gray-200 bg-white p-1.5">
                            {filteredChoiceUnitList.length === 0 ? (
                              <p className="py-3 text-center text-xs text-gray-400">
                                조건에 맞는 유닛이 없습니다.{' '}
                                {hasUnitFilter && (
                                  <button
                                    type="button"
                                    onClick={resetUnitFilters}
                                    className="text-gray-500 underline"
                                  >
                                    필터 초기화
                                  </button>
                                )}
                              </p>
                            ) : null}
                            {filteredChoiceUnitList.map((unit) => {
                              const isSelected = selectedChoiceUnits.includes(unit._id);
                              const brandName =
                                unit.giftItemBrand &&
                                typeof unit.giftItemBrand === 'object'
                                  ? unit.giftItemBrand.name
                                  : '';
                              return (
                                <label
                                  key={unit._id}
                                  className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2 ${
                                    isSelected
                                      ? 'border-indigo-300 bg-indigo-50'
                                      : 'border-gray-200 bg-white hover:bg-gray-50'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedChoiceUnits((prev) => [
                                          ...prev,
                                          unit._id,
                                        ]);
                                      } else {
                                        setSelectedChoiceUnits((prev) =>
                                          prev.filter((id) => id !== unit._id),
                                        );
                                      }
                                    }}
                                    className="h-4 w-4"
                                  />
                                  {unit.image && typeof unit.image === 'object' && (
                                    <img
                                      src={fileBucketUrl(unit.image.url, 'raw')}
                                      alt={unit.name}
                                      className="h-8 w-8 rounded object-cover"
                                    />
                                  )}
                                  <div className="flex-1">
                                    <p className="text-sm font-medium">
                                      {unit.name}
                                      {unit.enabled === false && (
                                        <span className="ml-1 rounded bg-gray-200 px-1 py-px text-xs font-normal text-gray-600">
                                          판매중단
                                        </span>
                                      )}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      {brandName && (
                                        <span className="text-xs text-gray-500">
                                          {brandName}
                                        </span>
                                      )}
                                      {unit.priceRegular != null && (
                                        <span className="text-xs text-gray-500">
                                          {unit.priceRegular.toLocaleString()}원
                                        </span>
                                      )}
                                      {unit.priceWhole != null && (
                                        <span className="text-xs text-gray-400">
                                          도매 {unit.priceWhole.toLocaleString()}원
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <a
                                    href={`/gift-seller/choice-unit/${unit._id}/edit`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="shrink-0 rounded px-1.5 py-0.5 text-xs text-gray-400 hover:bg-gray-100 hover:text-indigo-600"
                                  >
                                    수정
                                  </a>
                                </label>
                              );
                            })}
                          </div>
                        ) : choiceUnitListStatus === 'loading' ? (
                          <p className="text-xs text-gray-500">로딩 중...</p>
                        ) : (
                          <p className="text-xs text-gray-500">
                            등록된 선택유닛이 없습니다.{' '}
                            <a
                              href="/gift-seller/choice-unit/new"
                              className="text-indigo-600 underline"
                            >
                              선택유닛 등록
                            </a>
                          </p>
                        )}

                        {/* 선택된 유닛 순서 관리 */}
                        {selectedChoiceUnits.length > 0 && (
                          <div className="mt-3 border-t border-gray-200 pt-3">
                            <label className="mb-1 block text-xs font-medium text-gray-700">
                              표시 순서 (위에서부터 순서대로 표시)
                            </label>
                            <div className="space-y-1">
                              {selectedChoiceUnits.map((unitId, idx) => {
                                const unit = choiceUnitList.find((u) => u._id === unitId);
                                if (!unit) return null;
                                return (
                                  <div
                                    key={unitId}
                                    className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5"
                                  >
                                    <span className="w-5 text-center text-xs font-medium text-gray-500">
                                      {idx + 1}
                                    </span>
                                    {unit.image && typeof unit.image === 'object' && (
                                      <img
                                        src={fileBucketUrl(unit.image.url, 'raw')}
                                        alt={unit.name}
                                        className="h-6 w-6 rounded object-cover"
                                      />
                                    )}
                                    <span className="flex-1 truncate text-xs">
                                      {unit.name}
                                    </span>
                                    <div className="flex gap-0.5">
                                      <button
                                        type="button"
                                        disabled={idx === 0}
                                        onClick={() => {
                                          setSelectedChoiceUnits((prev) => {
                                            const next = [...prev];
                                            const tmp = next[idx - 1] as string;
                                            next[idx - 1] = next[idx] as string;
                                            next[idx] = tmp;
                                            return next;
                                          });
                                        }}
                                        className="rounded px-1.5 py-0.5 text-xs text-indigo-600 hover:bg-indigo-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                                      >
                                        ▲
                                      </button>
                                      <button
                                        type="button"
                                        disabled={idx === selectedChoiceUnits.length - 1}
                                        onClick={() => {
                                          setSelectedChoiceUnits((prev) => {
                                            const next = [...prev];
                                            const tmp = next[idx] as string;
                                            next[idx] = next[idx + 1] as string;
                                            next[idx + 1] = tmp;
                                            return next;
                                          });
                                        }}
                                        className="rounded px-1.5 py-0.5 text-xs text-indigo-600 hover:bg-indigo-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                                      >
                                        ▼
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setSelectedChoiceUnits((prev) =>
                                            prev.filter((id) => id !== unitId),
                                          );
                                        }}
                                        className="rounded px-1.5 py-0.5 text-xs text-red-500 hover:bg-red-50"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <p className="mt-1 text-xs text-gray-400">
                              ▲▼ 버튼으로 순서를 변경하세요. 이 순서대로 고객에게
                              표시됩니다.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </section>
              <section
                id="item-delivery"
                aria-labelledby="item-delivery-heading"
                className="scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
              >
                <div className="mb-5">
                  <h2 id="item-delivery-heading" className="text-base font-semibold">
                    {formData.type === GiftItemInfoType.Delivery
                      ? '배송 · 감사카드'
                      : '쿠폰 정보'}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    상품에 적용할 상세 설정을 확인하세요.
                  </p>
                </div>
                <div className="space-y-5">
                  {/* 쿠폰 상세 정보 (쿠폰형일 때만) */}
                  {formData.type === GiftItemInfoType.Coupon && (
                    <div className="rounded border bg-gray-50 p-3">
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        쿠폰 상세 정보
                      </label>
                      <div className="space-y-2">
                        <div>
                          <label
                            htmlFor="item-couponValidPeriod"
                            className="mb-0.5 block text-xs text-gray-600"
                          >
                            유효기간
                          </label>
                          <input
                            type="text"
                            id="item-couponValidPeriod"
                            name="couponValidPeriod"
                            value={formData.couponValidPeriod}
                            onChange={handleChange}
                            placeholder="예: 발행일로부터 3개월"
                            className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="item-couponPlaceOfExchange"
                            className="mb-0.5 block text-xs text-gray-600"
                          >
                            교환처
                          </label>
                          <input
                            type="text"
                            id="item-couponPlaceOfExchange"
                            name="couponPlaceOfExchange"
                            value={formData.couponPlaceOfExchange}
                            onChange={handleChange}
                            placeholder="예: 전국 CU 편의점"
                            className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* 배송 상세 정보 (배송형일 때만) */}
                  {formData.type === GiftItemInfoType.Delivery && (
                    <div className="rounded border bg-gray-50 p-3">
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        배송 상세 정보
                      </label>
                      {selectedBrand?.deliveryDetail && (
                        <p className="mb-2 text-xs text-gray-500">
                          브랜드 기본값이 설정되어 있습니다. 비워두면 브랜드 기본값이
                          적용됩니다.
                        </p>
                      )}

                      {/* 개별배송 허용 여부 */}
                      <div className="mb-3 flex items-start justify-between gap-2 rounded border border-gray-200 bg-white p-2">
                        <div>
                          <label
                            htmlFor="item-allowIndividualDelivery"
                            className="text-sm font-medium text-gray-700"
                          >
                            개별배송 허용
                          </label>
                          <p className="text-xs text-gray-500">
                            끄면 이 상품은 일괄배송(Bulk) 전용이 되어 개별배송 선택지가
                            숨겨집니다.
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          id="item-allowIndividualDelivery"
                          name="allowIndividualDelivery"
                          checked={formData.allowIndividualDelivery}
                          onChange={handleChange}
                          className="mt-0.5 h-5 w-5"
                        />
                      </div>

                      <div className="space-y-2">
                        <div>
                          <label
                            htmlFor="item-deliveryShippingInfo"
                            className="mb-0.5 block text-xs text-gray-600"
                          >
                            배송정보/기간 안내
                          </label>
                          <textarea
                            id="item-deliveryShippingInfo"
                            name="deliveryShippingInfo"
                            value={formData.deliveryShippingInfo}
                            onChange={handleChange}
                            placeholder={
                              selectedBrand?.deliveryDetail?.shippingInfo ||
                              '예: 주문 후 2~3일 이내 출고'
                            }
                            className="h-20 w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="item-deliveryShippingFeeInfo"
                            className="mb-0.5 block text-xs text-gray-600"
                          >
                            배송비 안내
                          </label>
                          <textarea
                            id="item-deliveryShippingFeeInfo"
                            name="deliveryShippingFeeInfo"
                            value={formData.deliveryShippingFeeInfo}
                            onChange={handleChange}
                            placeholder={
                              selectedBrand?.deliveryDetail?.shippingFeeInfo ||
                              '예: 무료배송 / 3,000원'
                            }
                            className="h-20 w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label
                              htmlFor="item-deliveryShippingFee"
                              className="mb-0.5 block text-xs text-gray-600"
                            >
                              소량주문 추가금 (개당)
                            </label>
                            <input
                              type="number"
                              id="item-deliveryShippingFee"
                              name="deliveryShippingFee"
                              value={formData.deliveryShippingFee}
                              onChange={handleChange}
                              min={0}
                              placeholder={
                                selectedBrand?.deliveryDetail?.shippingFee != null
                                  ? `브랜드 기본 ${selectedBrand.deliveryDetail.shippingFee}원`
                                  : '예: 500'
                              }
                              className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                            />
                          </div>
                          <div>
                            <label
                              htmlFor="item-deliveryFreeShippingMinQuantity"
                              className="mb-0.5 block text-xs text-gray-600"
                            >
                              추가금 면제 최소수량
                            </label>
                            <input
                              type="number"
                              id="item-deliveryFreeShippingMinQuantity"
                              name="deliveryFreeShippingMinQuantity"
                              value={formData.deliveryFreeShippingMinQuantity}
                              onChange={handleChange}
                              min={0}
                              placeholder={
                                selectedBrand?.deliveryDetail?.freeShippingMinQuantity !=
                                null
                                  ? `브랜드 기본 ${selectedBrand.deliveryDetail.freeShippingMinQuantity}개`
                                  : '예: 20'
                              }
                              className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-400">
                          이 수량 미만 주문 시 <b>개당</b> 추가금이 단가에
                          붙습니다(이상이면 면제). 비워두면 브랜드 기본값이 적용됩니다.
                        </p>
                      </div>
                    </div>
                  )}
                  {/* 감사카드 설정 (배송형 + 브랜드에 감사인쇄 설정이 있을 때만) */}
                  {formData.type === GiftItemInfoType.Delivery &&
                    selectedBrand?.thanksPrint && (
                      <div className="rounded border bg-gray-50 p-3">
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          감사카드 설정
                        </label>
                        <p className="mb-2 text-xs text-gray-500">
                          브랜드 기본:{' '}
                          {selectedBrand.thanksPrint.enabled ? '사용' : '미사용'} · 템플릿{' '}
                          {brandThanksTemplates.length}개
                        </p>

                        <div className="mb-3">
                          <label
                            htmlFor="item-useThanksPrint"
                            className="mb-0.5 block text-xs text-gray-600"
                          >
                            감사카드 사용 여부
                          </label>
                          <select
                            id="item-useThanksPrint"
                            name="useThanksPrint"
                            value={formData.useThanksPrint}
                            onChange={handleChange}
                            className="w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                          >
                            <option value="">브랜드 따름 (기본)</option>
                            <option value="true">사용</option>
                            <option value="false">미사용</option>
                          </select>
                        </div>

                        {brandThanksTemplates.length > 0 && (
                          <div>
                            <label className="mb-0.5 block text-xs text-gray-600">
                              허용 템플릿 제한
                            </label>
                            <p className="mb-1.5 text-xs text-gray-400">
                              아무것도 체크하지 않으면 브랜드 <b>전체</b> 템플릿이
                              허용됩니다. 체크하면 이 상품은 체크한 템플릿만 선택할 수
                              있습니다 (이후 브랜드에 새 템플릿이 추가돼도 자동 허용되지
                              않음).
                            </p>
                            <div className="space-y-1">
                              {brandThanksTemplates.map((tpl, idx) => {
                                const id = tpl._id;
                                if (!id) return null;
                                const presetCount = (tpl.messagePresets ?? []).filter(
                                  (p) => p.trim(),
                                ).length;
                                return (
                                  <label
                                    key={id}
                                    className="flex cursor-pointer items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={thanksPrintTemplateIds.includes(id)}
                                      onChange={() => toggleThanksTemplate(id)}
                                      className="h-4 w-4"
                                    />
                                    <span className="text-sm text-gray-700">
                                      {tpl.name || `템플릿 ${idx + 1}`}
                                    </span>
                                    {tpl.enabled === false && (
                                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500">
                                        숨김
                                      </span>
                                    )}
                                    {presetCount > 0 && (
                                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                                        지정문구 {presetCount}개
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              </section>
              <section
                id="item-images"
                aria-labelledby="item-images-heading"
                className="scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
              >
                <div className="mb-5">
                  <h2 id="item-images-heading" className="text-base font-semibold">
                    상품 이미지
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    대표 이미지와 상세페이지에 표시할 이미지를 관리합니다.
                  </p>
                </div>
                <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
                  {/* 대표 이미지 */}
                  <div className="min-w-0">
                    <ImageUploadBox
                      title="대표 이미지"
                      setFile={setImageFile}
                      defaultImageUrl={defaultImageUrl}
                    />
                  </div>
                  {/* 헤더 이미지 (정사각형, carousel용) */}
                  <div className="min-w-0">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      헤더 이미지 (정사각형, carousel용)
                    </label>
                    <p className="mb-1 text-xs text-gray-500">
                      상세페이지 상단에 스크롤 형태로 표시됩니다. 업로드하면 기존 이미지
                      뒤에 추가되며, ◀▶ 로 순서를 바꿀 수 있습니다.
                    </p>
                    <Button
                      type="button"
                      className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white"
                      onClick={() => {
                        setHeaderImageUploaderOpen(true);
                      }}
                    >
                      이미지 추가
                    </Button>

                    {/* 기존+신규 헤더 이미지 (순서 변경/삭제 가능) */}
                    {headerImageItems.length > 0 ? (
                      <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                        {headerImageItems.map((item, idx) => (
                          <div key={item.key} className="relative flex-shrink-0">
                            <img
                              src={imageItemUrl(item, 'raw')}
                              alt={`헤더이미지 ${idx + 1}`}
                              className="h-20 w-20 rounded object-cover"
                            />
                            <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                              {idx + 1}
                            </span>
                            {item.file && (
                              <span className="absolute right-1 top-1 rounded bg-gray-500 px-1 text-xs text-white">
                                new
                              </span>
                            )}
                            <div className="absolute inset-x-0 bottom-0 flex justify-center gap-0.5 rounded-b bg-black/50 py-0.5">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() =>
                                  moveImageItem(setHeaderImageItems, item.key, -1)
                                }
                                className="px-1 text-xs text-white disabled:text-white/30"
                              >
                                ◀
                              </button>
                              <button
                                type="button"
                                disabled={idx === headerImageItems.length - 1}
                                onClick={() =>
                                  moveImageItem(setHeaderImageItems, item.key, 1)
                                }
                                className="px-1 text-xs text-white disabled:text-white/30"
                              >
                                ▶
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  removeImageItem(setHeaderImageItems, item.key)
                                }
                                className="px-1 text-xs text-red-300 hover:text-red-200"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-gray-400">
                        등록된 헤더 이미지가 없습니다.
                      </p>
                    )}
                  </div>
                  {/* 상세 이미지 */}
                  <div className="min-w-0">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      상세 이미지 (세로로 이어붙임)
                    </label>
                    <p className="mb-1 text-xs text-gray-500">
                      상세페이지 하단에 순서대로 세로로 표시됩니다. 업로드하면 기존 이미지
                      뒤에 추가되며, ▲▼ 로 순서를 바꿀 수 있습니다.
                    </p>
                    <Button
                      type="button"
                      className="rounded bg-indigo-600 px-3 py-1.5 text-sm text-white"
                      onClick={() => setDetailImageUploaderOpen(true)}
                    >
                      이미지 추가
                    </Button>

                    {/* 기존+신규 상세 이미지 (순서 변경/삭제 가능) */}
                    {detailImageItems.length > 0 ? (
                      <div className="mt-2 space-y-1">
                        {detailImageItems.map((item, idx) => (
                          <div key={item.key} className="relative">
                            <img
                              src={imageItemUrl(item, 'w400')}
                              alt={`상세이미지 ${idx + 1}`}
                              className="w-full rounded object-cover"
                            />
                            <span className="absolute left-1 top-1 rounded bg-black/50 px-1 text-xs text-white">
                              {idx + 1}
                            </span>
                            {item.file && (
                              <span className="absolute left-7 top-1 rounded bg-gray-500 px-1 text-xs text-white">
                                new
                              </span>
                            )}
                            <div className="absolute right-1 top-1 flex gap-0.5 rounded bg-black/50 px-0.5 py-0.5">
                              <button
                                type="button"
                                disabled={idx === 0}
                                onClick={() =>
                                  moveImageItem(setDetailImageItems, item.key, -1)
                                }
                                className="px-1 text-xs text-white disabled:text-white/30"
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                disabled={idx === detailImageItems.length - 1}
                                onClick={() =>
                                  moveImageItem(setDetailImageItems, item.key, 1)
                                }
                                className="px-1 text-xs text-white disabled:text-white/30"
                              >
                                ▼
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  removeImageItem(setDetailImageItems, item.key)
                                }
                                className="px-1 text-xs text-red-300 hover:text-red-200"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-1 text-xs text-gray-400">
                        등록된 상세 이미지가 없습니다.
                      </p>
                    )}
                  </div>
                </div>
              </section>
              {/* 추가옵션 (배송형 전용) */}
              {formData.type === GiftItemInfoType.Delivery && (
                <section
                  id="item-options"
                  aria-labelledby="item-options-heading"
                  className="scroll-mt-6 rounded-md border border-gray-200 bg-white p-5 sm:p-6"
                >
                  <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 id="item-options-heading" className="text-base font-semibold">
                        추가옵션
                      </h2>
                      <p className="mt-0.5 text-xs text-gray-500">
                        비누종류·향초종류처럼 선택종류별 추가금이 붙는 옵션입니다.
                        추가금이 0이면 무료로 표시됩니다. 없으면 비워두세요.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="button"
                        onClick={handleCopyOptions}
                        disabled={additionalOptions.length === 0}
                        className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                      >
                        복사
                      </Button>
                      <Button
                        type="button"
                        onClick={handlePasteOptions}
                        disabled={clipboardCount === 0}
                        className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-40"
                      >
                        붙여넣기{clipboardCount > 0 ? ` (${clipboardCount})` : ''}
                      </Button>
                      <Button
                        type="button"
                        onClick={addOption}
                        className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:bg-gray-50"
                      >
                        + 옵션 그룹 추가
                      </Button>
                    </div>
                  </div>

                  {additionalOptions.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-400">
                      등록된 추가옵션이 없습니다.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {additionalOptions.map((opt, optIdx) => (
                        <div
                          key={opt.key}
                          className="rounded-lg border border-gray-200 bg-gray-50 p-3"
                        >
                          {/* 옵션 그룹 헤더 */}
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-700">
                              옵션 {optIdx + 1}
                            </span>
                            <input
                              type="text"
                              aria-label={`옵션 ${optIdx + 1} 이름`}
                              value={opt.name}
                              onChange={(e) =>
                                updateOption(opt.key, { name: e.target.value })
                              }
                              placeholder="옵션명 (예: 비누종류)"
                              className="min-w-[140px] flex-1 rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                            />
                            <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs text-gray-600">
                              <input
                                type="checkbox"
                                checked={opt.required}
                                onChange={(e) =>
                                  updateOption(opt.key, { required: e.target.checked })
                                }
                                className="h-4 w-4"
                              />
                              필수 선택
                            </label>
                            <div className="flex shrink-0 gap-0.5">
                              <button
                                type="button"
                                disabled={optIdx === 0}
                                aria-label={`옵션 ${optIdx + 1} 위로 이동`}
                                onClick={() => moveOption(opt.key, -1)}
                                className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                disabled={optIdx === additionalOptions.length - 1}
                                aria-label={`옵션 ${optIdx + 1} 아래로 이동`}
                                onClick={() => moveOption(opt.key, 1)}
                                className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
                              >
                                ▼
                              </button>
                              <button
                                type="button"
                                aria-label={`옵션 ${optIdx + 1} 삭제`}
                                onClick={() => removeOption(opt.key)}
                                className="rounded px-1.5 py-0.5 text-xs text-red-500 hover:bg-red-50"
                              >
                                ✕
                              </button>
                            </div>
                          </div>

                          {/* 선택 횟수(슬롯) 설정 */}
                          <div className="mt-2 flex flex-wrap items-center gap-2 rounded border border-dashed border-gray-200 bg-gray-50 px-2 py-1.5">
                            <span className="text-xs font-medium text-gray-600">
                              선택 횟수
                            </span>
                            <input
                              type="number"
                              min={1}
                              max={10}
                              aria-label={`옵션 ${optIdx + 1} 선택 횟수`}
                              value={opt.selectionCount}
                              onChange={(e) => {
                                const n = Math.max(
                                  1,
                                  Math.min(10, Math.floor(Number(e.target.value)) || 1),
                                );
                                updateOption(opt.key, { selectionCount: n });
                              }}
                              className="w-16 rounded border bg-white px-2 py-1 text-sm disabled:bg-gray-50"
                            />
                            <span className="text-xs text-gray-400">
                              2 이상이면 같은 선택종류를 횟수만큼 각각 고릅니다 (예:
                              비누종류1·비누종류2)
                            </span>
                          </div>

                          {/* 슬롯 라벨 (선택 횟수 2 이상일 때) */}
                          {opt.selectionCount > 1 && (
                            <div className="mt-2">
                              <p className="mb-1 text-xs text-gray-500">
                                슬롯 라벨 (비우면 「{opt.name || '옵션'}1·2…」 자동 생성)
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {Array.from({ length: opt.selectionCount }).map(
                                  (_, si) => (
                                    <input
                                      key={si}
                                      type="text"
                                      value={opt.slotLabels[si] ?? ''}
                                      onChange={(e) => {
                                        const labels = Array.from(
                                          { length: opt.selectionCount },
                                          (__, i) => opt.slotLabels[i] ?? '',
                                        );
                                        labels[si] = e.target.value;
                                        updateOption(opt.key, { slotLabels: labels });
                                      }}
                                      placeholder={`${opt.name || '옵션'}${si + 1}`}
                                      className="w-32 rounded border bg-white px-2 py-1 text-sm disabled:bg-gray-50"
                                    />
                                  ),
                                )}
                              </div>
                            </div>
                          )}

                          {/* 선택종류 목록 */}
                          <div className="mt-3 space-y-2">
                            {opt.choices.map((c, cIdx) => {
                              const imgUrl = choiceImageUrl(c);
                              return (
                                <div
                                  key={c.key}
                                  className={`rounded-lg border border-gray-200 bg-white p-2 ${
                                    c.enabled ? '' : 'opacity-60'
                                  }`}
                                >
                                  <div className="flex flex-wrap items-center gap-2">
                                    {/* 설명 이미지 업로드 */}
                                    <label className="relative flex h-12 w-12 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-400 hover:border-indigo-400">
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                          const f = e.target.files?.[0];
                                          if (f) handleChoiceFile(opt.key, c.key, f);
                                        }}
                                        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                      />
                                      {imgUrl ? (
                                        <img
                                          src={imgUrl}
                                          alt={c.name}
                                          className="h-full w-full object-cover"
                                        />
                                      ) : (
                                        <span>이미지</span>
                                      )}
                                    </label>

                                    <input
                                      type="text"
                                      aria-label={`옵션 ${optIdx + 1} 선택종류 ${
                                        cIdx + 1
                                      } 이름`}
                                      value={c.name}
                                      onChange={(e) =>
                                        updateChoice(opt.key, c.key, {
                                          name: e.target.value,
                                        })
                                      }
                                      placeholder="선택종류명 (예: 골든)"
                                      className="min-w-[120px] flex-1 rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                                    />
                                    <div className="flex shrink-0 items-center gap-1">
                                      <span className="text-xs text-gray-400">+</span>
                                      <input
                                        type="number"
                                        aria-label={`${c.name || '선택종류'} 추가금`}
                                        value={c.priceDelta}
                                        onChange={(e) =>
                                          updateChoice(opt.key, c.key, {
                                            priceDelta: Number(e.target.value),
                                          })
                                        }
                                        min={0}
                                        placeholder="추가금"
                                        className="w-24 rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                                      />
                                      <span className="text-xs text-gray-400">원</span>
                                    </div>
                                    <label className="flex shrink-0 items-center gap-1 text-xs text-gray-500">
                                      <input
                                        type="color"
                                        value={c.colorHex || '#000000'}
                                        onChange={(e) =>
                                          updateChoice(opt.key, c.key, {
                                            colorHex: e.target.value,
                                          })
                                        }
                                        className="h-7 w-7 cursor-pointer rounded border border-gray-300 bg-white p-0.5"
                                        title="색상칩"
                                      />
                                      {c.colorHex ? (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            updateChoice(opt.key, c.key, { colorHex: '' })
                                          }
                                          className="underline"
                                        >
                                          제거
                                        </button>
                                      ) : (
                                        <span>색상</span>
                                      )}
                                    </label>
                                    <label className="flex shrink-0 cursor-pointer items-center gap-1 text-xs text-gray-600">
                                      <input
                                        type="checkbox"
                                        checked={c.enabled}
                                        onChange={(e) =>
                                          updateChoice(opt.key, c.key, {
                                            enabled: e.target.checked,
                                          })
                                        }
                                        className="h-4 w-4"
                                      />
                                      노출
                                    </label>
                                    <div className="flex shrink-0 gap-0.5">
                                      <button
                                        type="button"
                                        disabled={cIdx === 0}
                                        aria-label={`${c.name || '선택종류'} 위로 이동`}
                                        onClick={() => moveChoice(opt.key, c.key, -1)}
                                        className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                                      >
                                        ▲
                                      </button>
                                      <button
                                        type="button"
                                        disabled={cIdx === opt.choices.length - 1}
                                        aria-label={`${c.name || '선택종류'} 아래로 이동`}
                                        onClick={() => moveChoice(opt.key, c.key, 1)}
                                        className="rounded px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-100 disabled:text-gray-300 disabled:hover:bg-transparent"
                                      >
                                        ▼
                                      </button>
                                      <button
                                        type="button"
                                        aria-label={`${c.name || '선택종류'} 삭제`}
                                        onClick={() => removeChoice(opt.key, c.key)}
                                        className="rounded px-1.5 py-0.5 text-xs text-red-500 hover:bg-red-50"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  </div>

                                  {/* 한 줄 설명 (전체 폭) */}
                                  <input
                                    type="text"
                                    aria-label={`${c.name || '선택종류'} 설명`}
                                    value={c.description}
                                    onChange={(e) =>
                                      updateChoice(opt.key, c.key, {
                                        description: e.target.value,
                                      })
                                    }
                                    placeholder="설명 (선택) — 예: 은은한 라벤더 향, 천연 유래 성분"
                                    className="mt-2 w-full rounded border bg-white px-2 py-1.5 text-sm disabled:bg-gray-50"
                                  />
                                </div>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => addChoice(opt.key)}
                              className="w-full rounded-lg border border-dashed border-gray-300 py-2 text-xs font-medium text-gray-500 hover:border-indigo-400 hover:text-indigo-600"
                            >
                              + 선택종류 추가
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}
            </fieldset>
            <div className="sticky bottom-0 z-10 mt-6 border-t border-gray-200 bg-white px-5 py-4 shadow-[0_-4px_12px_rgba(0,0,0,0.03)]">
              {saveError && (
                <p role="alert" className="mb-3 text-sm text-red-600">
                  {saveError}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  {isEdit ? (
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={loading}
                      className="rounded px-2 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      상품 삭제
                    </button>
                  ) : (
                    <p className="text-xs text-gray-500">
                      입력한 내용을 확인한 후 등록해주세요.
                    </p>
                  )}
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => navigate('/gift-seller/item-info')}
                    className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleSubmit}
                    className="rounded bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {loading ? '저장 중...' : isEdit ? '수정하기' : '등록하기'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* 헤더 이미지 업로더 모달 */}
      <ImageUploader
        openId={headerImageOpenId}
        title="헤더 이미지 업로드 (정사각형)"
        onSubmit={onHeaderImageUploaderFunc}
        useName={false}
      />

      {/* 상세 이미지 업로더 모달 */}
      <ImageUploader
        openId={openId}
        title="상세이미지 업로드"
        onSubmit={onDetailImageUploaderFunc}
        useName={false}
      />
    </div>
  );
};
